import { useField } from 'react-final-form';

import { Switch, Typography, Box } from '@occc-app/design-system';
import type { FlagDefinition } from '@occc/data-flags';
import { User_Role } from '@occc/graphql';

import { featureFlagEditor } from './flags';
import { toFieldName } from './use-feature-access-form.hook';

type FeatureFlagRowProps = {
  flag: FlagDefinition;
  userUuid: string;
  currentUserUuid: string;
  targetUserRole: User_Role;
};

export function FeatureFlagRow({
  flag,
  userUuid,
  currentUserUuid,
  targetUserRole,
}: FeatureFlagRowProps) {
  const fieldName = toFieldName(flag.key);
  const { input } = useField<boolean>(fieldName, { type: 'checkbox' });
  const enabled = input.checked ?? false;

  const isEditorFlag = flag.key === featureFlagEditor.key;
  const isSelf = userUuid === currentUserUuid;
  const selfLocked = isSelf && isEditorFlag && enabled;
  const nonAdminLocked = isEditorFlag && targetUserRole !== User_Role.Admin;
  const locked = selfLocked || nonAdminLocked;

  const helperMessage = selfLocked
    ? 'This setting controls your access to this editor. Only another administrator can change it.'
    : nonAdminLocked
    ? 'Only administrator accounts can receive access to this editor.'
    : null;

  return (
    <Box
      component='label'
      display='flex'
      alignItems='center'
      gap={2}
      py={2.5}
      px={3}
      sx={{
        cursor: locked ? 'default' : 'pointer',
        transition: 'background-color 0.15s',
        '&:hover': locked ? undefined : { backgroundColor: 'action.hover' },
      }}
      htmlFor={flag.key}
    >
      <Box display='flex' flexDirection='column' gap={0.5} sx={{ flex: 1, minWidth: 0 }}>
        <Box display='flex' alignItems='center' gap={1}>
          <Typography variant='subtitle1' sx={{ lineHeight: 1.4 }}>
            {flag.title}
          </Typography>
          <Typography
            variant='caption'
            component='span'
            sx={(theme) => ({
              py: 0.25,
              px: 0.75,
              borderRadius: '4px',
              lineHeight: 1.3,
              whiteSpace: 'nowrap',
              color: enabled ? theme.palette.success.contrastText : theme.palette.grey[600],
              backgroundColor: enabled ? theme.palette.success.light : theme.palette.grey[200],
            })}
          >
            {enabled ? 'On' : 'Off'}
          </Typography>
        </Box>
        <Typography variant='body2' color='textSecondary' sx={{ lineHeight: 1.5 }}>
          {flag.description}
        </Typography>
        {helperMessage && (
          <Typography variant='caption' sx={{ mt: 0.5, color: 'warning.main' }}>
            {helperMessage}
          </Typography>
        )}
      </Box>
      <Switch
        id={flag.key}
        name={flag.key}
        checked={enabled}
        onChange={input.onChange}
        disabled={locked}
        sx={{ flexShrink: 0 }}
      />
    </Box>
  );
}
